import { BaseMapper, type IBulkMapper, type IPaginationMapper } from './BaseMapper';
import type { components } from '@/core/types/generated/api';

// Import DTO types
export type UserDto = components['schemas']['UserDto'];
export type CreateUserCommand = components['schemas']['CreateUserCommand'];
export type UpdateUserCommand = components['schemas']['UpdateUserCommand'];
export type BulkCreateUsersRequest = components['schemas']['BulkCreateUsersRequest'];
export type BulkDeleteUsersRequest = components['schemas']['BulkDeleteUsersRequest'];
export type BulkUpdateUserStatusRequest = components['schemas']['BulkUpdateUserStatusRequest'];
export type BulkUpdateUserRoleRequest = components['schemas']['BulkUpdateUserRoleRequest'];
export type UserDtoBulkOperationResult = components['schemas']['UserDtoBulkOperationResult'];
export type ApiSystemRole = components['schemas']['SystemRole'];
export type ApiUserStatus = components['schemas']['UserStatus'];

// UI Model interface (extending the base User model with optional properties)
export interface UiUser {
  // Core/common fields
  id?: string;
  username?: string;
  name?: string; // legacy UI field
  email: string;
  fullName: string; // required to match core/api-mappers.UiUser
  designation?: string | null;
  // Role/status compatibility: keep both representations so this type is
  // structurally compatible with code importing either shape.
  role?: ApiSystemRole | number | null;
  roleLabel?: string; // Human label based on API enum
  status?: ApiUserStatus;
  statusLabel?: string;
  isActive?: boolean;
  lastLogin?: Date | null;
  // Timestamps and audit
  createdAt?: Date;
  createdBy?: string | null;
  lastModifiedAt?: Date | null;
  lastModifiedBy?: string | null;
  updatedAt?: Date;
  // Additional collections
  projectMemberships?: any[] | null;
}

/**
 * UserMapper - Maps between UserDto and UiUser
 */
export class UserMapper extends BaseMapper<UserDto, UiUser> 
  implements IBulkMapper<UserDto, UiUser>, IPaginationMapper {
  
  mapToUiModel(dto: UserDto): UiUser {
    return {
      id: dto.id || '',
      name: dto.fullName || dto.email || '',
      email: dto.email || '',
      designation: this.mapString(dto.designation),
      role: dto.role,
      roleLabel: this.toEnumLabel(dto.role),
      status: dto.isActive === false ? 'Disabled' : 'Active',
      statusLabel: this.toEnumLabel(dto.isActive === false ? 'Disabled' as ApiUserStatus : 'Active'),
      lastLogin: this.mapDate(dto.lastLogin) || new Date(),
      updatedAt: this.mapDate(dto.lastModifiedAt) || new Date(),
      // Additional properties
      username: this.mapUsername(dto),
      fullName: dto.fullName || '',
      isActive: this.mapBoolean(dto.isActive, true),
      createdAt: this.mapDate(dto.createdAt) || undefined,
      createdBy: dto.createdBy,
      lastModifiedAt: this.mapDate(dto.lastModifiedAt),
      lastModifiedBy: dto.lastModifiedBy,
      projectMemberships: dto.projectMemberships
    };
  }

  mapToDto(uiModel: Partial<UiUser>): Partial<UserDto> {
    const role = (uiModel.role as ApiSystemRole | undefined) ??
      this.labelToEnum<ApiSystemRole>(uiModel.roleLabel, ['Administrator','User'] as const);
    return {
      email: uiModel.email,
      fullName: uiModel.fullName || uiModel.name,
      designation: uiModel.designation,
      isActive: uiModel.isActive,
      role: role,
    };
  }

  mapBulkResult(result: { successes: UserDto[]; errors: any[] }): { successes: UiUser[]; errors: any[] } {
    return {
      successes: this.mapToUiModels(result.successes),
      errors: result.errors
    };
  }

  mapPaginationInfo(paginationData: any): {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } {
    return {
      totalItems: paginationData?.totalItems || 0,
      totalPages: paginationData?.totalPages || 1,
      currentPage: paginationData?.currentPage || 1,
      pageSize: paginationData?.pageSize || 10,
      hasNext: paginationData?.hasNext || false,
      hasPrevious: paginationData?.hasPrevious || false
    };
  }

  /**
   * Generate username from email if not provided
   */
  private mapUsername(dto: UserDto): string | undefined {
    // Username may not exist on DTO; derive from email safely
    const email = dto.email;
    if (typeof email === 'string') {
      const at = email.indexOf('@');
      return at > 0 ? email.slice(0, at) : email;
    }
    return undefined;
  }

  // No numeric mapping needed: API expects string enums now. Keep helpers minimal.
}

// Export singleton instance
export const userMapper = new UserMapper();
